/**
 * Read-only WebSocket hook for the public analytics viewer.
 *
 * Connects to `/ws/public-share` (token as subprotocol) without authentication.
 * Listens for confirmed events and undone events from the match room,
 * but never sends any data.
 */
import { useEffect, useRef, useCallback, useState } from "react";
import type { MatchEvent } from "../store/useMatchLogStore";

const INVALID_LINK_CLOSE_CODE = 1008;
const INVALID_LINK_REASON_PATTERN =
  /invalid|not\s*found|revok|share\s*token|expired|forbidden/i;

const resolveWsBaseUrl = () => {
  const explicit = import.meta.env.VITE_WS_URL;
  if (explicit) {
    return explicit.replace(/\/+$/, "");
  }

  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    const sanitized = apiUrl.replace(/\/+$/, "");
    const withoutApi = sanitized.replace(/\/api\/v1$/, "");
    if (withoutApi.startsWith("https://")) {
      return withoutApi.replace("https://", "wss://");
    }
    if (withoutApi.startsWith("http://")) {
      return withoutApi.replace("http://", "ws://");
    }
  }

  if (typeof window !== "undefined") {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}`;
  }

  return "ws://localhost:8000";
};

interface UsePublicMatchSocketProps {
  shareToken: string;
  enabled?: boolean;
  onNewEvent?: (event: MatchEvent) => void;
  onUndoEvent?: (eventId: string) => void;
  onMatchUpdate?: () => void;
  onUnhandledMessage?: () => void;
}

interface UsePublicMatchSocketReturn {
  isConnected: boolean;
  connectionState:
    | "idle"
    | "connecting"
    | "connected"
    | "reconnecting"
    | "invalid_link";
  isInvalidLink: boolean;
}

const isInvalidLinkClose = (event: CloseEvent): boolean => {
  if (event.code === INVALID_LINK_CLOSE_CODE) {
    return true;
  }
  return INVALID_LINK_REASON_PATTERN.test(event.reason ?? "");
};

export function usePublicMatchSocket({
  shareToken,
  enabled = true,
  onNewEvent,
  onUndoEvent,
  onMatchUpdate,
  onUnhandledMessage,
}: UsePublicMatchSocketProps): UsePublicMatchSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<
    "idle" | "connecting" | "connected" | "reconnecting" | "invalid_link"
  >("idle");
  const [isInvalidLink, setIsInvalidLink] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbacksRef = useRef({
    onNewEvent,
    onUndoEvent,
    onMatchUpdate,
    onUnhandledMessage,
  });
  callbacksRef.current = {
    onNewEvent,
    onUndoEvent,
    onMatchUpdate,
    onUnhandledMessage,
  };

  const connect = useCallback(
    (isReconnect = false) => {
      if (!shareToken || !enabled) {
        setIsConnected(false);
        setConnectionState("idle");
        setIsInvalidLink(false);
        return;
      }

      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }

      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }

      setConnectionState(isReconnect ? "reconnecting" : "connecting");

      const baseUrl = resolveWsBaseUrl();
      // EPIC-55: token as the sole subprotocol, never in the URL (the URL
      // is logged by Cloud Run). Do not log `url` or `shareToken`.
      const url = `${baseUrl}/ws/public-share`;

      const ws = new WebSocket(url, [shareToken]);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setConnectionState("connected");
        setIsInvalidLink(false);
      };

      ws.onclose = (event) => {
        setIsConnected(false);

        if (isInvalidLinkClose(event)) {
          setIsInvalidLink(true);
          setConnectionState("invalid_link");
          return;
        }

        if (!enabled) {
          setConnectionState("idle");
          return;
        }

        setConnectionState("reconnecting");
        reconnectTimer.current = setTimeout(() => connect(true), 5000);
      };

      ws.onerror = () => {
        ws.close();
      };

      ws.onmessage = (messageEvent) => {
        try {
          const data = JSON.parse(messageEvent.data);

          // Confirmed event broadcast
          if (data._confirmed && data.match_id) {
            callbacksRef.current.onNewEvent?.(data as MatchEvent);
            return;
          }

          // Undone event broadcast
          if (data.type === "event_undone" && data.event_id) {
            callbacksRef.current.onUndoEvent?.(data.event_id);
            return;
          }

          // Match state update — backend broadcasts "match_state_changed"
          // for status transitions, clock mode changes, score updates, etc.
          if (
            data.type === "match_state_changed" ||
            data.type === "match_update"
          ) {
            callbacksRef.current.onMatchUpdate?.();
            return;
          }

          callbacksRef.current.onUnhandledMessage?.();
        } catch {
          // Ignore parse errors
        }
      };
    },
    [shareToken, enabled],
  );

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        const ws = wsRef.current;
        wsRef.current = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
      }
    };
  }, [connect]);

  return { isConnected, connectionState, isInvalidLink };
}
